import { countBy, min, nonNullable } from 'lib/array/arrayUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getRoomTypeCapacity } from './tourUtils'

/**
 * Returns the cheapest available option for the departure for the travellers given
 */
export function getDepartureOptionForTravellers(
  departure: Tours.TourV2OfferDeparture,
  travellers: App.Occupants,
): Tours.TourV2OfferPurchasableOption | undefined {
  const guestCount = sumUpOccupancies([travellers])
  const viableOptions = departure.options.filter(option => {
    const minGuests = getRoomTypeCapacity(option.roomType)
    const fitsRoomSize = guestCount === minGuests
    const hasEnoughInventory = !!option.inventoryLeft

    return hasEnoughInventory && fitsRoomSize
  })
  return min(viableOptions, option => option.memberPrice || option.price)
}

interface TourRoomOptionView {
  room: App.Occupants;
  option: Tours.TourV2OfferPurchasableOption;
}

/**
 * Returns a list of options that are available to for the set of travellers given
 * If there are no room combinations that fit all travellers, will return an empty list
 */
export function getDepartureOptionsForTravellers(
  departure: Tours.TourV2OfferDeparture,
  travellers: Array<App.Occupants>,
): Array<TourRoomOptionView> {
  const options = travellers.map<TourRoomOptionView | undefined>(traveller => {
    const option = getDepartureOptionForTravellers(departure, traveller)
    if (option) {
      return {
        option,
        room: traveller,
      }
    }
  })
  const definedOptions = nonNullable(options)
  if (options.length !== definedOptions.length) {
    // one of the traveller sets doesn't have an option available to it, so it's not available
    return []
  }
  // make sure each option has enough total inventory
  const optionsByRequiredRooms = countBy(definedOptions, option => option.option)
  if (optionsByRequiredRooms.entries().some((([option, count]) => option.inventoryLeft < count))) {
    return []
  }

  return definedOptions
}

export type DepartureSoldOutReason = 'sold-out' | 'travellers';

/**
 * Determines whether or not a list of departures are sold out
 * and gives the reason why they are sold out
 * @param departures The list of departures to check
 * @param travellers Optionally include travellers to check capacity
 * @returns Whether or not it's sold out and why
 */
export function areDeparturesSoldOut(
  departures: Array<Tours.TourV2OfferDeparture>,
  travellers?: Array<App.Occupants>,
): DepartureSoldOutReason | undefined {
  const soldOut = departures.every(dep => {
    if (travellers) {
      return getDepartureOptionsForTravellers(dep, travellers).length === 0
    }
    return dep.options.length === 0
  })
  let reason: DepartureSoldOutReason | undefined
  if (soldOut) {
    reason = departures.every(dep => dep.options.length === 0) ? 'sold-out' : 'travellers'
  }

  return reason
}
